// De lade van Koppelingen om zelf te kiezen, zoals in het prototype (KoppelingenLade): wat gekoppeld wordt met de
// stand ervan, een paar gegevens, dan de keuzelijst met zoeken (vanaf 9 keuzes), eventueel in groepen, en Koppelen.
// Gebruikt voor een taak (Clockify-taak), een klantplan (Clockify-project) en een maildomein (klant).
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Icon } from "../lib/icons";

export interface PickOpt {
  key: string;
  label: string;
  note?: string;
  pill?: string;
  pillCls?: string;
  /** zichtbaar maar niet te kiezen (al gebruikt, of voor support) */
  disabled?: boolean;
  /** kopje boven deze keuze als het verschilt van de vorige */
  group?: string;
  /** blijft staan bij zoeken (Nieuwe Clockify-taak, Geen klant) */
  keep?: boolean;
}

export function KoppelPicker({ eyebrow, title, state, kv, listTitle, options, initial, note, confirmText, busy, ready, onConfirm, onClose, extra, children }: {
  eyebrow: string;
  title: string;
  /** de stand nu: tekst en pill-klasse */
  state: [string, string];
  kv: [string, string][];
  listTitle: string;
  options: PickOpt[];
  initial: string | null;
  note: string;
  confirmText: (sel: string | null) => string;
  busy: boolean;
  /** zonder keuzelijst (een veld): of Bevestigen kan */
  ready?: boolean;
  onConfirm: (sel: string | null) => void;
  onClose: () => void;
  /** meteen onder de gekozen keuze (de schatting bij een nieuwe Clockify-taak) */
  extra?: (sel: string | null) => ReactNode;
  /** boven de lijst (een invoerveld) */
  children?: ReactNode;
}) {
  const [sel, setSel] = useState<string | null>(initial);
  const [q, setQ] = useState("");
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const qq = q.trim().toLowerCase();
  const shown = options.filter((o) => o.keep || !qq || [o.label, o.note || "", o.group || ""].join(" ").toLowerCase().includes(qq));
  const valid = options.some((o) => o.key === sel && !o.disabled) ? sel : null;
  const can = options.length ? !!valid : ready !== false;
  let lastGroup: string | undefined;

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="kpt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>{eyebrow}</p><h2 id="kpt">{title}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={state[1]}><span className="ex-dot" />{state[0]}</span>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs">
            {kv.map(([k, v]) => <div key={k} className="p-kv"><span>{k}</span><b>{v}</b></div>)}
          </div>
          <div className="p-fs"><h3>{listTitle}</h3>
            {children}
            {options.length > 8 && <label className="ex-search p-kp-q"><Icon name="search" size={14} /><input type="text" placeholder="Zoek in de lijst" aria-label="Zoek in de lijst" value={q} onChange={(e) => setQ(e.target.value)} /></label>}
            {options.length > 0 && (
              <div className="p-capc" role="radiogroup" aria-label={listTitle}>
                {shown.map((o) => {
                  const head = o.group && o.group !== lastGroup ? o.group : null;
                  lastGroup = o.group;
                  const on = valid === o.key;
                  return (
                    <Fragment key={o.key}>
                      {head && <div className="p-kp-grp">{head}</div>}
                      <button type="button" role="radio" aria-checked={on} aria-disabled={o.disabled || undefined} className={"p-cand p-kp-opt" + (on ? " on" : "") + (o.disabled ? " off" : "")}
                        onClick={() => { if (!o.disabled) setSel(o.key); }}>
                        <span className={"p-radio" + (on ? " on" : "")} />
                        <span className="p-kp-opt-t"><b title={o.label}>{o.label}</b>{o.note && <span className="m">{o.note}</span>}</span>
                        <span>{o.pill && <span className={o.pillCls || "ex-pill ex-pill--off"}>{o.pill}</span>}</span>
                      </button>
                      {on && extra?.(o.key)}
                    </Fragment>
                  );
                })}
                {!shown.length && <p className="p-empty">Niets gevonden voor {q.trim()}.</p>}
              </div>
            )}
            <p className="p-kd-note" style={{ margin: "12px 0 0" }}>{note}</p>
          </div>
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>De app bewaart de koppeling, met naam en tijd</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Annuleren</button>
            <button type="button" className="ex-btn ex-btn--primary" disabled={busy || !can} onClick={() => onConfirm(valid)}><Icon name="check" size={15} />{confirmText(valid)}</button>
          </span>
        </div>
      </aside>
    </>
  );
}
